import React, { useMemo, useState } from 'react';
import { Button, Card, Form, Input, message, Typography } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';

const { Paragraph, Title } = Typography;

const PASSWORD_RULE_TEXT = 'Use at least 8 chars with uppercase, lowercase, number, and special character.';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const handleSubmit = async (values: { new_password: string }) => {
    if (!token) {
      message.error('Reset link is missing or invalid.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.post<{ message: string }>('/auth/password-reset/confirm', {
        token,
        new_password: values.new_password,
      });
      message.success(res.message || 'Password updated successfully.');
      navigate('/user/login', { replace: true });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : error?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#f3f6fb' }}>
      <Card style={{ width: '100%', maxWidth: 440, borderRadius: 16 }}>
        <Title level={3} style={{ marginTop: 0 }}>Set New Password</Title>
        <Paragraph type="secondary">
          Enter a new password for your account. This reset link can be used once and expires automatically.
        </Paragraph>
        <Form layout="vertical" onFinish={handleSubmit} autoComplete="off">
          <Form.Item
            name="new_password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
              {
                validator: async (_, value) => {
                  const v = String(value || '');
                  if (!v) return Promise.resolve();
                  if (!/[A-Z]/.test(v)) return Promise.reject(new Error('Include at least one uppercase letter'));
                  if (!/[a-z]/.test(v)) return Promise.reject(new Error('Include at least one lowercase letter'));
                  if (!/\d/.test(v)) return Promise.reject(new Error('Include at least one number'));
                  if (!/[^A-Za-z0-9]/.test(v)) return Promise.reject(new Error('Include at least one special character'));
                  return Promise.resolve();
                },
              },
            ]}
            extra={PASSWORD_RULE_TEXT}
          >
            <Input.Password size="large" placeholder="Enter new password" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Confirm Password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                  return Promise.reject(new Error('The two passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder="Confirm new password" autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" size="large" block loading={loading} disabled={!token}>
            Update Password
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default ResetPassword;
